/**
 * Avatar API Helpers
 * Functions for fetching and updating user avatar from the backend API
 */

import type { AxiosResponse } from 'axios';
import { API_ENDPOINTS } from '../../../constants';
import { AVATAR_IDS, type AvatarId } from '../../../constants/avatarIds';
import type { EquippedMap } from '../../../components/avatars/LayeredAvatarLab/characterCustomizer.types';
import { logger } from '../../helpers/logger.helper';

type ApiClientFn = (endpoint: string, config?: unknown) => Promise<AxiosResponse>;

// Set for O(1) lookup performance (created once at module level)
const VALID_AVATAR_IDS_SET = new Set(Object.values(AVATAR_IDS));

/**
 * Fetch user's avatarId from the API
 * @param userId - The user ID
 * @param apiClientFn - Required authenticated API client function (from useApiClient hook)
 * @returns The user's avatarId or null if not found/error
 */
export const fetchUserAvatar = async (
  userId: string,
  apiClientFn: (endpoint: string, config?: unknown) => Promise<AxiosResponse>
): Promise<AvatarId | null> => {
  if (!apiClientFn) {
    logger.error('[fetchUserAvatar] apiClientFn is required for authenticated requests', {
      userId,
    });
    return null;
  }

  const endpoint = `${API_ENDPOINTS.users.profile}/${userId}`;

  try {
    const response = await apiClientFn(endpoint);

    // Axios response: check response.data instead of response.ok
    if (response.status >= 400) {
      // 404 is expected for new users before profile/avatar exists
      if (response.status === 404) {
        logger.warn('[fetchUserAvatar] Profile not found (e.g. new user)', {
          userId,
          endpoint,
        });
      } else {
        logger.error('[fetchUserAvatar] API error response', {
          userId,
          endpoint,
          status: response.status,
        });
      }
      return null;
    }

    const data = response.data;

    if (data?.success && data.data?.profile?.avatarId) {
      const avatarId = data.data.profile.avatarId;
      
      // Validate that it's a valid AvatarId using Set for O(1) lookup
      if (VALID_AVATAR_IDS_SET.has(avatarId)) {
        return avatarId as AvatarId;
      } else {
        logger.warn('[fetchUserAvatar] Invalid avatarId returned from API', {
          userId,
          avatarId,
        });
      }
    }
    
    return null;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const status = (error as { response?: { status?: number } })?.response?.status;
    const isAuthError = errorMessage.includes('No active session') ||
                       errorMessage.includes('AUTH_TIMEOUT');
    const isNotFound = status === 404 || errorMessage.includes('404');

    if (isAuthError) {
      logger.error('[fetchUserAvatar] Authentication error', {
        userId,
        errorMessage,
      });
    } else if (isNotFound) {
      logger.warn('[fetchUserAvatar] Profile not found (e.g. new user)', {
        userId,
        errorMessage,
      });
    } else {
      logger.error('[fetchUserAvatar] Error fetching avatar', {
        userId,
        errorMessage,
      });
    }

    return null;
  }
};

/**
 * Update user's avatarId via API
 * @param userId - The user ID
 * @param avatarId - The avatar ID to set
 * @param apiClientFn - Required authenticated API client function (from useApiClient hook)
 * @returns The updated avatarId from server response, or null if error
 */
export const updateUserAvatar = async (
  userId: string,
  avatarId: AvatarId,
  apiClientFn: (endpoint: string, config?: unknown) => Promise<AxiosResponse>
): Promise<AvatarId | null> => {
  logger.info('[updateUserAvatar] Starting avatar update', {
    userId,
    avatarId,
  });

  if (!apiClientFn) {
    logger.error('[updateUserAvatar] apiClientFn is required for authenticated requests', {
      userId,
      avatarId,
    });
    return null;
  }

  try {
    const endpoint = API_ENDPOINTS.users.profile;
    logger.info('[updateUserAvatar] Making API request', {
      userId,
      avatarId,
      endpoint,
      method: 'PUT',
    });

    const response = await apiClientFn(endpoint, {
      method: 'PUT',
      data: { avatarId },
    });

    logger.info('[updateUserAvatar] API response received', {
      userId,
      avatarId,
      status: response.status,
      hasData: !!response.data,
      responseKeys: response ? Object.keys(response) : [],
    });

    // Log the raw response structure to see if apiClient transformed it
    logger.info('[updateUserAvatar] Raw response structure', {
      userId,
      avatarId,
      rawResponseStringified: JSON.stringify(response, null, 2),
      responseDataType: typeof response,
    });

    if (response.status >= 400) {
      logger.error('[updateUserAvatar] API returned error status', {
        userId,
        avatarId,
        status: response.status,
        responseData: response.data,
      });
      return null;
    }

    const data = response.data;
    logger.info('[updateUserAvatar] Parsing response data', {
      userId,
      avatarId,
      hasSuccess: !!data?.success,
      hasProfile: !!data?.data?.profile,
      profileAvatarId: data?.data?.profile?.avatarId,
    });

    // Log the FULL response structure to debug response format issues
    logger.info('[updateUserAvatar] Full response data structure', {
      userId,
      avatarId,
      fullResponseData: JSON.stringify(data, null, 2),
      responseDataType: typeof data,
      responseDataKeys: data ? Object.keys(data) : [],
      responseDataStringified: data ? JSON.stringify(data) : 'null',
    });

    // Parse response: data.success && data.data?.profile?.avatarId
    if (data?.success && data.data?.profile?.avatarId) {
      const updatedAvatarId = data.data.profile.avatarId;
      logger.info('[updateUserAvatar] Validating returned avatarId', {
        userId,
        requestedAvatarId: avatarId,
        returnedAvatarId: updatedAvatarId,
        isValid: VALID_AVATAR_IDS_SET.has(updatedAvatarId),
      });

      // Validate that it's a valid AvatarId using Set for O(1) lookup
      if (VALID_AVATAR_IDS_SET.has(updatedAvatarId)) {
        logger.info('[updateUserAvatar] Avatar update successful', {
          userId,
          requestedAvatarId: avatarId,
          returnedAvatarId: updatedAvatarId,
        });
        return updatedAvatarId as AvatarId;
      } else {
        logger.warn('[updateUserAvatar] Returned avatarId is invalid', {
          userId,
          requestedAvatarId: avatarId,
          returnedAvatarId: updatedAvatarId,
          validIds: Array.from(VALID_AVATAR_IDS_SET),
        });
      }
    } else {
      logger.warn('[updateUserAvatar] Response structure is invalid', {
        userId,
        avatarId,
        responseStructure: {
          hasSuccess: !!data?.success,
          successValue: data?.success,
          hasData: !!data?.data,
          hasProfile: !!data?.data?.profile,
          profileAvatarId: data?.data?.profile?.avatarId,
        },
        fullResponse: data,
      });
    }
    return null;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorStack = error instanceof Error ? error.stack : undefined;
    logger.error('[updateUserAvatar] Exception during avatar update', {
      userId,
      avatarId,
      errorMessage,
      errorStack,
    });
    return null;
  }
};

/**
 * Persist a custom layered look (the customizer's equipped map). Unlike the ready-avatar
 * path, this does NOT validate against VALID_AVATAR_IDS — the server validates the config
 * against the catalog. Returns the saved config on success, or null on error.
 */
export const updateUserAvatarConfig = async (
  userId: string,
  avatarConfig: EquippedMap,
  apiClientFn: ApiClientFn
): Promise<EquippedMap | null> => {
  if (!apiClientFn) {
    logger.error('[updateUserAvatarConfig] apiClientFn is required', { userId });
    return null;
  }
  try {
    const response = await apiClientFn(API_ENDPOINTS.users.profile, {
      method: 'PUT',
      data: { avatarConfig },
    });
    if (response.status >= 400) {
      logger.error('[updateUserAvatarConfig] API error status', { userId, status: response.status });
      return null;
    }
    const saved = response.data?.data?.profile?.avatarConfig;
    if (saved && typeof saved === 'object' && !Array.isArray(saved)) {
      return saved as EquippedMap;
    }
    logger.warn('[updateUserAvatarConfig] Response missing avatarConfig', { userId });
    return null;
  } catch (error) {
    logger.error('[updateUserAvatarConfig] Exception during avatar config update', {
      userId,
      errorMessage: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
};

